import React from 'react';
import { motion } from 'framer-motion';
import { RefreshCw, Zap } from 'lucide-react';

export default function Refund() {
  return (
    <div className="min-h-screen bg-black pt-32 pb-20">
      <div className="max-w-3xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-600/10 border border-red-600/20 mb-6">
            <RefreshCw size={14} className="text-red-600" />
            <span className="text-[10px] font-black tracking-[0.3em] uppercase text-red-600">Refund Policy</span>
          </div>

          <h1 className="text-5xl md:text-6xl font-black italic uppercase leading-[0.85] text-white tracking-tighter mb-6">
            REFUND <span className="text-gold">POLICY</span>
          </h1>

          <p className="text-zinc-400 text-lg">Last updated: May 12, 2026</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8 text-zinc-300"
        >
          <section>
            <h2 className="text-2xl font-black text-white mb-4 uppercase">Overview</h2>
            <p className="leading-relaxed">
              At Chaitanya Shooting Academy, we want you to be completely satisfied with our training programs. This refund policy outlines the conditions under which refunds or credits are available for our services.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-black text-white mb-4 uppercase">1. Trial Sessions</h2>
            <div className="p-6 bg-zinc-900/50 border-l-4 border-red-600 rounded-r-2xl">
              <p className="mb-3"><strong className="text-white">First Trial Session: FREE & Non-Refundable</strong></p>
              <p className="text-sm">
                Your free trial session is our way of introducing you to our academy. It is complimentary and cannot be refunded or credited.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-black text-white mb-4 uppercase">2. Monthly Subscriptions</h2>
            
            <div className="space-y-4">
              <div className="p-5 bg-white/5 border border-white/10 rounded-2xl">
                <h4 className="text-white font-bold mb-3">Cancellation Timeline</h4>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-3">
                    <span className="text-gold font-bold mt-1">✓</span>
                    <span><strong className="text-white">2 weeks before billing:</strong> Full refund minus ₹500 admin fee</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-gold font-bold mt-1">✓</span>
                    <span><strong className="text-white">Less than 2 weeks:</strong> No refund, current month credited to next</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-yellow-600 font-bold mt-1">✗</span>
                    <span><strong className="text-white">After sessions completed:</strong> No refund available</span>
                  </li>
                </ul>
              </div>

              <div className="p-5 bg-white/5 border border-white/10 rounded-2xl">
                <h4 className="text-white font-bold mb-3">Example Calculation</h4>
                <div className="text-sm space-y-2">
                  <p>Program: ₹10,000/month</p>
                  <p>Cancellation Request: 20 days before billing (within 2 weeks window)</p>
                  <p className="text-gold"><strong>Refund: ₹10,000 - ₹500 = ₹9,500</strong></p>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-black text-white mb-4 uppercase">3. Annual Memberships</h2>
            <ul className="space-y-3 list-disc list-inside">
              <li><strong className="text-white">First 30 days:</strong> Full refund minus ₹1,000 admin fee</li>
              <li><strong className="text-white">31-90 days:</strong> 75% refund of unused portion</li>
              <li><strong className="text-white">After 90 days:</strong> 50% refund of unused portion</li>
              <li><strong className="text-white">After 6 months:</strong> No refund (50% annual discount benefit applied)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-black text-white mb-4 uppercase">4. What's NOT Refundable</h2>
            <ul className="space-y-2 list-disc list-inside">
              <li>Sessions already completed or attended</li>
              <li>Makeup sessions that have been scheduled</li>
              <li>Special coaching fees for competition preparation</li>
              <li>Referral bonuses or discounts applied</li>
              <li>Video analysis or recorded sessions</li>
              <li>Non-attendance (missed sessions without cancellation notice)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-black text-white mb-4 uppercase">5. Refund Process</h2>
            <div className="space-y-4">
              <div className="p-5 bg-white/5 border border-white/10 rounded-2xl">
                <h4 className="text-white font-bold mb-3">Step-by-Step</h4>
                <ol className="space-y-3 text-sm">
                  <li className="flex items-start gap-3">
                    <span className="bg-red-600 text-white w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">1</span>
                    <span>Submit cancellation request via email with your membership details</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="bg-red-600 text-white w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">2</span>
                    <span>We confirm receipt and calculate refund amount (2-3 business days)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="bg-red-600 text-white w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">3</span>
                    <span>Refund processed to original payment method (5-7 business days)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="bg-red-600 text-white w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">4</span>
                    <span>Access to academy facilities revoked after refund confirmation</span>
                  </li>
                </ol>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-black text-white mb-4 uppercase">6. Special Circumstances</h2>
            <div className="space-y-4">
              <div className="p-5 bg-green-600/10 border border-green-600/30 rounded-2xl">
                <h4 className="text-green-400 font-bold mb-2">Medical/Injury Issues</h4>
                <p className="text-sm">
                  If you cannot train due to medical reasons, provide a doctor's certificate. We offer 100% refund for the current month and credit towards future training when you recover.
                </p>
              </div>

              <div className="p-5 bg-green-600/10 border border-green-600/30 rounded-2xl">
                <h4 className="text-green-400 font-bold mb-2">Relocation</h4>
                <p className="text-sm">
                  If you relocate outside Gurugram, we offer 50% refund of unused portion or credit towards online coaching.
                </p>
              </div>

              <div className="p-5 bg-green-600/10 border border-green-600/30 rounded-2xl">
                <h4 className="text-green-400 font-bold mb-2">Academy Issues</h4>
                <p className="text-sm">
                  If we cancel or significantly modify programs, full refund is issued automatically. If you're unsatisfied with training quality, discuss with our management team.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-black text-white mb-4 uppercase">7. Payment Method Reversals</h2>
            <p className="leading-relaxed mb-4">
              If you dispute a charge with your bank or credit card company (chargeback):
            </p>
            <ul className="space-y-2 list-disc list-inside">
              <li>Your membership is immediately suspended</li>
              <li>You forfeit any remaining credits</li>
              <li>You may be restricted from future enrollment</li>
              <li>We reserve the right to pursue legal action for disputed legitimate charges</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-black text-white mb-4 uppercase">8. Refund Credits</h2>
            <p className="leading-relaxed">
              Instead of cash refunds, you may request credits towards:
            </p>
            <ul className="space-y-2 list-disc list-inside">
              <li>Future training programs (valid for 12 months)</li>
              <li>Online coaching sessions</li>
              <li>Special workshops or masterclasses</li>
              <li>Upgrade to higher-tier programs</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-black text-white mb-4 uppercase">9. How to Request Refund</h2>
            <div className="p-6 bg-zinc-900/50 border border-white/10 rounded-2xl space-y-3">
              <p><strong className="text-white">Email:</strong> refunds@chaitanyashooting.com</p>
              <p><strong className="text-white">Phone:</strong> +91 96535 24055</p>
              <p><strong className="text-white">In Person:</strong> Visit our academy with membership proof</p>
              <p className="text-sm text-zinc-400">Include: Membership ID, dates, reason for refund request</p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-black text-white mb-4 uppercase">10. Policy Changes</h2>
            <p className="leading-relaxed">
              This refund policy may be updated periodically. Changes apply to new memberships. Existing members are notified 30 days before significant changes take effect.
            </p>
          </section>

          <section className="bg-blue-600/10 border border-blue-600/30 p-6 rounded-2xl">
            <p className="text-sm text-blue-300">
              <strong className="text-blue-400">Note:</strong> All refund amounts are in Indian Rupees (₹). Processing may take 5-7 business days depending on your bank. Refunds are non-transferable and applicable only to the original payment method.
            </p>
          </section>
        </motion.div>
      </div>
    </div>
  );
}
